import React, { useEffect } from "react"
import moment, { Moment } from "moment"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"

import PageContainer from "~/library/PageContainer"
import BookingServiceReport from "~/views/Reports/BookingServiceReport"
import PageTitle from "~/library/PageTitle"
import { useReportSearchParams } from "~/hooks/useReportSearchParams"

type TimeRange = "today" | "week" | "month" | "custom"
type ReportType = "booking-service"

const ReportsView = () => {
  const { type, range, startDate, endDate, setReportSearchParams } = useReportSearchParams()

  const reportType = (type as ReportType) || "booking-service"
  const timeRange = (range as TimeRange) || "week"
  const customStartDate = startDate ? moment(startDate) : null
  const customEndDate = endDate ? moment(endDate) : null

  useEffect(() => {
    if (!type || !range) {
      const defaultType = "booking-service" as ReportType
      const defaultRange = "week" as TimeRange

      setReportSearchParams({
        type: type || defaultType,
        range: range || defaultRange,
      })
    }
  }, [type, range])

  const reportTypeOptions: { label: string; value: ReportType }[] = [
    { label: "Booking Service", value: "booking-service" },
  ]

  const timeRangeOptions = [
    { label: "Today", value: "today" },
    { label: "This Week", value: "week" },
    { label: "This Month", value: "month" },
    { label: "Custom Range", value: "custom" },
  ]

  const getDateRangeFor = (range: TimeRange) => {
    switch (range) {
      case "today":
        return {
          startDate: moment().startOf("day").format("YYYY-MM-DD"),
          endDate: moment().endOf("day").format("YYYY-MM-DD"),
        }
      case "week":
        return {
          startDate: moment().startOf("week").format("YYYY-MM-DD"),
          endDate: moment().endOf("week").format("YYYY-MM-DD"),
        }
      case "month":
        return {
          startDate: moment().startOf("month").format("YYYY-MM-DD"),
          endDate: moment().endOf("month").format("YYYY-MM-DD"),
        }
      case "custom":
        return startDate && endDate
          ? {
              startDate,
              endDate,
            }
          : null
      default:
        return {
          startDate: moment().startOf("week").format("YYYY-MM-DD"),
          endDate: moment().endOf("week").format("YYYY-MM-DD"),
        }
    }
  }

  const handleReportTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setReportSearchParams({ type: e.target.value as ReportType })
  }

  const handleTimeRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRange = e.target.value as TimeRange

    if (newRange !== "custom") {
      setReportSearchParams({ range: newRange, startDate: "", endDate: "" })
    } else {
      setReportSearchParams({ range: newRange })
    }
  }

  const handleCustomStartChange = (value: Moment | null) => {
    setReportSearchParams({
      range: "custom",
      startDate: value ? value.format("YYYY-MM-DD") : "",
    })
  }

  const handleCustomEndChange = (value: Moment | null) => {
    setReportSearchParams({
      range: "custom",
      endDate: value ? value.format("YYYY-MM-DD") : "",
    })
  }

  const ReportsPageControls = () => (
    <div className="d-flex gap-2 align-items-center">
      <select
        className="form-select"
        value={reportType}
        onChange={handleReportTypeChange}
        style={{ width: "200px" }}
      >
        {reportTypeOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <select
        className="form-select"
        value={timeRange}
        onChange={handleTimeRangeChange}
        style={{ maxWidth: "160px" }}
      >
        {timeRangeOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {timeRange === "custom" && (
        <>
          <DatePicker
            label="From"
            format="MM/DD/YYYY"
            sx={{
              width: 180,
            }}
            value={customStartDate}
            maxDate={customEndDate || undefined}
            slotProps={{
              field: { size: "small" },
            }}
            onChange={handleCustomStartChange}
          />
          <DatePicker
            label="To"
            format="MM/DD/YYYY"
            sx={{
              width: 180,
            }}
            value={customEndDate}
            minDate={customStartDate || undefined}
            slotProps={{
              field: { size: "small" },
            }}
            onChange={handleCustomEndChange}
          />
        </>
      )}
    </div>
  )

  const renderReportContent = () => {
    switch (reportType) {
      case "booking-service" as ReportType:
        return <BookingServiceReport {...getDateRangeFor(timeRange)} />
      default:
        return (
          <div className="card">
            <div className="card-body text-center py-5">
              <h5 className="text-muted">Report type not implemented yet</h5>
            </div>
          </div>
        )
    }
  }

  return (
    <PageContainer>
      <PageTitle title="Reports">
        <ReportsPageControls />
      </PageTitle>

      {renderReportContent()}
    </PageContainer>
  )
}

export default ReportsView
